import { useEffect } from "react";

/**
 * Closes Select/Menu/Popover surfaces that MUI rendered *without* a backdrop.
 *
 * MUI's `Modal` dismisses an outside click by hit-testing an invisible backdrop
 * it renders as the first child of the popover root. Under MUI 5 + React 19 that
 * child is intermittently absent (the root then contains only the focus-trap
 * sentinels and the paper), which leaves the surface with nothing to detect the
 * click — the dropdown stays open until Escape or a selection.
 *
 * This net only engages for that broken shape: an open popover whose backdrop is
 * missing or not hit-testable. When MUI's own backdrop is present we do nothing
 * so the library keeps ownership of dismissal. Closing is delegated to MUI by
 * dispatching Escape, which routes through `Modal`'s own `onClose` handler
 * instead of mutating state we do not own.
 *
 * Escape is deferred to a microtask so Modal focus-restoration does not cancel
 * the user's click (mousedown → focus move → lost click → "need to double-click"
 * on controls underneath, e.g. the dashboard role switcher).
 */

const SURFACE_SELECTOR =
  ".MuiPopover-root.MuiModal-root:not(.MuiModal-hidden):not(.MuiDialog-root):not(.MuiDrawer-root)";

const isHitTestableBackdrop = (surface: Element): boolean => {
  const backdrop = surface.querySelector(":scope > .MuiBackdrop-root");
  if (!backdrop) {
    return false;
  }
  const style = window.getComputedStyle(backdrop);
  return (
    style.visibility !== "hidden" &&
    style.display !== "none" &&
    style.pointerEvents !== "none"
  );
};

const closeViaEscape = (surface: Element): void => {
  // MUI binds `onKeyDown` to the popover root, so an Escape dispatched on the
  // focused element inside the trap bubbles into its documented close path.
  const focused = document.activeElement;
  const target = focused && surface.contains(focused) ? focused : surface;
  target.dispatchEvent(
    new KeyboardEvent("keydown", {
      key: "Escape",
      code: "Escape",
      bubbles: true,
      cancelable: true,
    }),
  );
};

export const useDropdownBackdropSafetyNet = (): void => {
  useEffect(() => {
    const handlePointerDown = (event: MouseEvent): void => {
      const surfaces = document.querySelectorAll(SURFACE_SELECTOR);
      if (surfaces.length === 0) {
        return;
      }

      const raw = event.target;
      if (!(raw instanceof Node)) {
        return;
      }
      const target = raw instanceof Element ? raw : raw.parentElement;

      const toClose: Element[] = [];
      surfaces.forEach((surface) => {
        // MUI can still dismiss this one itself.
        if (isHitTestableBackdrop(surface)) {
          return;
        }
        // Clicks landing on the menu itself are selections, not dismissals.
        const paper = surface.querySelector(".MuiPaper-root");
        if (target && paper?.contains(target)) {
          return;
        }
        // A click on the trigger toggles the surface on its own; closing here
        // too would immediately reopen or double-fire.
        const trigger = document.querySelector(
          '[aria-expanded="true"][aria-haspopup="listbox"], [aria-expanded="true"][aria-haspopup="menu"], [aria-expanded="true"][aria-haspopup="true"]',
        );
        if (target && trigger?.closest(".MuiInputBase-root, .MuiButtonBase-root")?.contains(target)) {
          return;
        }
        toClose.push(surface);
      });

      if (toClose.length === 0) {
        return;
      }

      // Defer so the current pointer → click gesture completes on the intended
      // target (role tab, nav link, etc.) before Modal restores focus.
      queueMicrotask(() => {
        toClose.forEach((surface) => {
          if (document.contains(surface) && !isHitTestableBackdrop(surface)) {
            closeViaEscape(surface);
          }
        });
      });
    };

    document.addEventListener("mousedown", handlePointerDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
    };
  }, []);
};

export default useDropdownBackdropSafetyNet;
